import { useEffect, useMemo, useState } from "react";
import { Community } from "../../@types/Community";
import {
  getCommunity,
  getCommunityAdmins,
  getCommunityMembers,
  getCommunityTokens,
  hideOrUnhideCommunityToken,
} from "../../services/apis/AccountApi/Community";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { useWallet } from "../../services/wallet/useWallet";
import { isAdminForCommunity } from "../../utils/roles";
import { isAddress } from "ethers/lib/utils";
import { PaginatedResponse } from "../../@types/PaginatedResponse";
import { EmptyKudosType } from "../Profile/EmptyKudos";

const useCommunity = (
  communityUniqId: string,
  prefetchedCommunity?: Community,
  isHidden: boolean = false,
) => {
  enum CommunityTab {
    Kudos = 0,
    Members = 1,
    Requests = 2,
  }

  const [selectedIndex, setSelectedIndex] = useState(CommunityTab.Kudos);
  const [tokens, setTokens] = useState<any[]>([]);
  const [tokensResponse, setTokensResponse] = useState<PaginatedResponse>({
    limit: 0,
    offset: 0,
    hasMore: true,
  });
  const [admins, setAdmins] = useState([]);
  const [adminsTotalCount, setAdminsTotalCount] = useState(0);
  const [members, setMembers] = useState<any[]>([]);
  const [membersTotalCount, setMembersTotalCount] = useState(0);
  const [membersResponse, setMembersResponse] = useState<PaginatedResponse>({
    limit: 0,
    offset: 0,
    hasMore: true,
  });
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState<Community>({
    description: "",
    displayId: "",
    logoUrl: "",
    uniqId: "",
    name: "",
    kudosCount: 0,
    uniqueOwnersCount: 0,
    mintedKudosCount: 0,
    discordUrl: "",
    websiteUrl: "",
  });
  const [topLevelError, setTopLevelError] = useState<EmptyKudosType>(null);
  const [tokensError, setTokensError] = useState<EmptyKudosType>(null);
  const [membersError, setMembersError] = useState<EmptyKudosType>(null);
  const { roles } = useWallet();

  const router = useRouter();
  const searchParams = useMemo(() => {
    return router.query;
  }, [router.query]);
  const setSearchParams = (params: any) => {
    const query = {
      ...searchParams,
      tab: params.tab,
      tokenId: params.tokenId,
    };

    if (!query.tokenId) {
      delete query.tokenId;
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      {
        shallow: true,
      },
    );
  };

  const tab = useMemo(
    () => (searchParams?.tab as string) ?? "",
    [searchParams?.tab],
  );

  useEffect(() => {
    const tabValue: CommunityTab =
      CommunityTab[tab as keyof typeof CommunityTab];
    changeIndex(tabValue);
  }, [tab]);

  /**
   * Changes the tab and updates the url param.
   * @param index
   */
  const changeIndex = (index: CommunityTab) => {
    setSelectedIndex(index);
    setSearchParams({
      tab: CommunityTab[index],
    });
  };

  const fetchCommunityTokens = async (limit: number = 12) => {
    const response = await getCommunityTokens(
      communityUniqId,
      isHidden,
      tokensResponse?.offset,
      limit,
    );
    setTokensResponse({
      offset: tokensResponse.offset + limit,
      limit,
      hasMore: response?.data.length > 0,
    });
    setTokens([...tokens, ...response.data]);
    return response;
  };

  const fetchCommunityAdmins = async () => {
    const response = await getCommunityAdmins(communityUniqId);
    setAdmins(response?.data);
    setAdminsTotalCount(response?.totalCount);
  };

  const fetchCommunityMembers = async (limit: number = 100) => {
    const response = await getCommunityMembers(
      communityUniqId,
      membersResponse?.offset,
      limit,
    );
    setMembers([...members, ...response.data]);
    setMembersResponse({
      offset: membersResponse.offset + limit,
      limit,
      hasMore: response?.data.length > 0,
    });
    setMembersTotalCount(response?.totalCount);
    return response;
  };

  useEffect(() => {
    const fetchCommunityData = async () => {
      try {
        // Community doesn't exist
        if (communityUniqId === null) {
          setTopLevelError("communityNotFound");
          setLoading(false);
          return;
        }

        setLoading(true);
        setTopLevelError(null);
        setTokensError(null);
        setMembersError(null);

        if (!communityUniqId === undefined) {
          return;
        }

        let community;
        if (!prefetchedCommunity?.name) {
          community = await getCommunity(communityUniqId);
        } else {
          community = prefetchedCommunity;
        }

        if (!community?.name) {
          setTopLevelError("communityNotFound");
          setLoading(false);
          return;
        }

        setCommunity(community);

        try {
          const response = await fetchCommunityTokens();
          if (response.data.length === 0) {
            setTokensError("communityHasNoTokens");
          }
        } catch (error: any) {
          toast.error(error.message);
          setTokensError("communityHasNoTokens");
          setLoading(false);
        }

        try {
          await fetchCommunityAdmins();
          const members = await fetchCommunityMembers();

          if (members?.totalCount === 0) {
            setMembersError("communityHasNoMembers");
          }
        } catch (error: any) {
          toast.error(error.message);
          setMembersError("communityHasNoMembers");
          setLoading(false);
        }

        if (!isHidden && (!tab || !Object.values(CommunityTab).includes(tab))) {
          changeIndex(CommunityTab.Kudos);
        }
        setLoading(false);
      } catch (error: any) {
        toast.error(error.message);
        setTopLevelError("communityNotFound");
        setLoading(false);
      }
    };
    fetchCommunityData();
  }, [communityUniqId]);

  const hideKudos = async (tokenId: string) => {
    try {
      await hideOrUnhideCommunityToken(community.uniqId, tokenId, true);
      setTokens(tokens.filter((token: any) => token.tokenId !== tokenId));
      toast.success("Kudos was hidden");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const unhideKudos = async (tokenId: string) => {
    try {
      await hideOrUnhideCommunityToken(community.uniqId, tokenId, false);
      setTokens(tokens.filter((token: any) => token.tokenId !== tokenId));
      toast.success("Kudos was unhidden");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const isAdmin = useMemo(
    () => isAdminForCommunity(roles, community.uniqId),
    [roles, community.uniqId],
  );

  /**
   * Switch tab off Requests if the user logs out.
   */
  useEffect(() => {
    if (!isAdmin && selectedIndex === CommunityTab.Requests) {
      changeIndex(0);
    }
  }, [isAdmin]);

  return {
    selectedIndex,
    setSelectedIndex,
    tokens,
    tokensResponse,
    community,
    members,
    membersTotalCount,
    membersResponse,
    topLevelError,
    tokensError,
    membersError,
    admins,
    adminsTotalCount,
    changeIndex,
    hideKudos,
    unhideKudos,
    isAdmin,
    loading,
    fetchCommunityTokens,
    fetchCommunityMembers,
    fetchCommunityAdmins,
  };
};

export default useCommunity;

const communityMemberUsernameSort = (a: any, b: any) => {
  // sort to show non-ETH-address usernames first,
  // and then show ETH-address usernames
  if (isAddress(a.username) && !isAddress(b.username)) {
    return 1;
  }
  if (!isAddress(a.username) && isAddress(b.username)) {
    return -1;
  }

  // otherwise they're both non-ETH-address usernames or ETH-address usernames
  return a.username > b.username ? 1 : a.username < b.username ? -1 : 0;
};
